import { ReactNode } from "react";
import { cn } from "@drpcrd/utils/shared";
import { Typography } from "../typography";

type Props = {
  title: string;
  count?: number;
  totalLabel?: string;
  fallback?: string;
  fallbackAction?: ReactNode;
  action?: ReactNode;
  highlight?: boolean;
  children?: ReactNode;
  className?: string;
};

/**
 * Presentational deal-board column shell — header with title, count and an
 * optional total, plus either an empty-state fallback or a scrollable list
 * of cards. Extracted from the brand app's deal board so it can be reused.
 */
export const DealBoardColumn = ({
  title,
  count = 0,
  totalLabel,
  fallback,
  fallbackAction,
  action,
  highlight = false,
  children,
  className,
}: Props) => (
  <div
    className={cn(
      "flex min-w-35 flex-1 flex-col rounded-2xl px-1 pt-2",
      highlight ? "bg-primary-50" : "bg-neutral-150",
      className,
    )}
  >
    <div className="mb-2 flex shrink-0 items-center gap-1">
      <Typography size="sm" weight="semibold" className="ml-1">
        {title}
      </Typography>
      {count > 0 && (
        <Typography
          size="sm"
          weight="normal"
          color="neutral"
          className="flex items-center"
        >
          {count}
        </Typography>
      )}
      {totalLabel && (
        <Typography
          size="sm"
          weight="normal"
          color="neutral"
          className="flex items-center"
        >
          · {totalLabel}
        </Typography>
      )}
    </div>

    {action && <div className="shrink-0">{action}</div>}

    {count > 0 ? (
      <div className="scrollbar-transparent mb-1 flex min-h-0 grow flex-col items-stretch gap-1 overflow-y-auto">
        {children}
      </div>
    ) : (
      fallback && (
        <div className="flex grow flex-col items-center justify-center gap-2">
          <Typography
            size="sm"
            color="neutral"
            weight="semibold"
            className="text-center"
          >
            {fallback}
          </Typography>
          {fallbackAction}
        </div>
      )
    )}
  </div>
);
